import React, { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import ProductLayout from './ProductLayout'
import SEO from '../../components/SEO'
import FamilyImageModal from '../../components/FamilyImageModal'
import useCompanyStats from '../../components/useCompanyStats'

const API = ''

const CustomProductsPage = () => {
    const { stats } = useCompanyStats()
    const [categories, setCategories] = useState([])
    const [families, setFamilies] = useState([])
    const [custAnswers, setCustAnswers] = useState({})
    const [selectedFamily, setSelectedFamily] = useState(null)

    useEffect(() => {
        axios.get(`${API}/api/categories`).then(r => setCategories(r.data)).catch(() => { })
        axios.get(`${API}/api/custom-product-families`).then(r => setFamilies(r.data)).catch(() => { })
    }, [])

    const custPick = (step, val) => {
        const newAnswers = { ...custAnswers, [step]: val }
        // Clear downstream
        for (let s = step + 1; s <= 4; s++) delete newAnswers[s]
        setCustAnswers(newAnswers)
    }

    const allAnswered = custAnswers[1] && custAnswers[2] && custAnswers[3] && custAnswers[4]

    const allNavCategories = useMemo(() => {
        const cats = [{ id: 'all', name: 'All Products', slug: 'all-products' }]
        categories.forEach(c => cats.push({ ...c, slug: `cat-${c.id}` }))
        cats.push({ id: 'custom', name: 'Custom Products', slug: 'custom-products' })
        return cats
    }, [categories])

    return (
        <ProductLayout>
            <SEO
                title="Custom Products"
                description="PFC can modify any of our standard designs or develop a new design from scratch to meet the needs of your drawing or specification."
            />
            {/* Hero */}
            <div className="pfc-hero">
                <div className="pfc-hero-top">
                    <div className="pfc-hero-top-inner">
                        <div className="pfc-hero-eye">PFC Engineering - Custom Components</div>
                        <div className="pfc-hero-title">Custom Products</div>
                        <div className="pfc-hero-sub pfc-hero-sub-wide">
                            PFC can modify any of our standard designs or develop a new design from scratch to meet the needs of your drawing or specification. We already work commonly in line sizes ranging from ¼” to 8”, pressure ratings up to 15,000 PSIG, and in both ambient and cryogenic temperature environments so our extensive design library helps keeps NRE low. We stay close through design, manufacturing, test, and installation to make sure you get full support for the entirety of your project.
                        </div>
                        <div className="pfc-hero-tags">
                            <span className="pfc-htag pfc-htag-p">Low NRE Costs</span>
                            <span className="pfc-htag pfc-htag-t">Flight + Ground</span>
                            <span className="pfc-htag pfc-htag-a">Design to Spec</span>
                            <span className="pfc-htag pfc-htag-p">Cryogenic Capable</span>
                        </div>
                    </div>
                </div>

            </div>

            {/* Category tabs — sticky under navbar */}
            <div className="pfc-page-tabs">
                {allNavCategories.map((cat, i) => (
                    <React.Fragment key={cat.id}>
                        {i > 0 && <span className="pfc-tab-sep">·</span>}
                        <Link
                            to={cat.slug === 'custom-products' ? '/products/custom' : cat.id === 'all' ? '/products/category/all' : `/products/category/${cat.id}`}
                            className={`pfc-page-tab${cat.id === 'custom' ? ' active' : ''}`}
                        >
                            {cat.name}
                        </Link>
                    </React.Fragment>
                ))}
            </div>

            {/* Two-column layout (stretched to single column since configurator is removed) */}
            <div className="pfc-two-col" style={{ gridTemplateColumns: '1fr' }}>
                <div className="pfc-left-col">
                    {/* Intro strip */}
                    <div className="pfc-intro-strip">
                        <div className="pfc-is-icon"><i className="ti ti-book-2"></i></div>
                        <div>
                            <div className="pfc-ps-title">Design Library Approach</div>
                            <div className="pfc-ps-sub">
                                PFC maintains an extensive library of proven valve and pressure control designs. Most custom requests start as a modification of an existing validated design - reducing risk, cost, and lead time significantly compared to a clean-sheet design.
                            </div>
                        </div>
                    </div>

                    {/* Process steps */}
                    <div>
                        <div className="pfc-section-label">Custom product process</div>
                        <div className="pfc-process-steps">
                            <div className="pfc-ps"><div className="pfc-ps-num"><i className="ti ti-clipboard-list"></i></div><div className="pfc-ps-title">Requirements</div><div className="pfc-ps-sub">Customer provides a specification or application details to a  PFC Applications Engineer. PFC reviews the application details and proposes a custom modified product or new design to meet the Customer requirements.</div></div>
                            <div className="pfc-ps"><div className="pfc-ps-num"><i className="ti ti-ruler-2"></i></div><div className="pfc-ps-title">Design</div><div className="pfc-ps-sub">PFC designs a component which fulfills the customer's requirements.</div></div>
                            <div className="pfc-ps"><div className="pfc-ps-num"><i className="ti ti-settings"></i></div><div className="pfc-ps-title">Manufacturing</div><div className="pfc-ps-sub">PFC manufactures the components. It is put through rigorous inspections. All documentation for assembly and test are generated.</div></div>
                            <div className="pfc-ps"><div className="pfc-ps-num"><i className="ti ti-truck-delivery"></i></div><div className="pfc-ps-title">Test & Delivery</div><div className="pfc-ps-sub">PFC communicates extensively with the customer to ensure all testing is performed as required for Acceptance testing and Qualification testing if required. PFC has onsite capability for high pressure, cryogenic, lifecycle, vibration plus many additional environmental tests.</div></div>
                        </div>
                    </div>

                    {/* Product families */}
                    <div>
                        <div className="pfc-section-label">Custom product families</div>
                        <div className="pfc-custom-grid">
                            {families.map(fam => (
                                <div
                                    className="pfc-custom-card"
                                    key={fam.id}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => setSelectedFamily(fam)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedFamily(fam) } }}
                                >
                                    <div className="pfc-cc-top">
                                        <div className={`pfc-cc-icon ${fam.icon_class}`}><i className={`ti ${fam.icon}`}></i></div>
                                        <div className="pfc-cc-name">{fam.name}</div>
                                        <div className="pfc-cc-desc">{fam.description}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* CTA */}
                    <div className="pfc-cta-row">
                        <div className="pfc-ctac">
                            <div className="pfc-ctac-icon ci-p"><i className="ti ti-file-description"></i></div>
                            <div className="pfc-ctac-title">Send your specification</div>
                            <div className="pfc-ctac-sub">Share your drawing or requirements - PFC will review and respond promptly.</div>
                            <Link to="/contactus" className="pfc-ctac-btn cbp">Contact us ↗</Link>
                        </div>
                        <div className="pfc-ctac">
                            <div className="pfc-ctac-icon ci-t"><i className="ti ti-messages"></i></div>
                            <div className="pfc-ctac-title">Talk to an application engineer</div>
                            <div className="pfc-ctac-sub">Discuss your application directly with one of our app engineers.<br /><span style={{ whiteSpace: 'nowrap' }}>(916) 626-3029</span></div>
                            <Link to="/contactus" className="pfc-ctac-btn cbo">Call now</Link>
                        </div>
                        <div className="pfc-ctac">
                            <div className="pfc-ctac-icon ci-a"><i className="ti ti-building-factory-2"></i></div>
                            <div className="pfc-ctac-title">Tour capabilities</div>
                            <div className="pfc-ctac-sub">Our 77,000+ sq ft facility houses state-of-the-art Mazak live-tool lathes, a 2,500 sq ft third party-certified ISO 7 (Class 10,000) hard-wall clean room, an 800 sq ft clean work area, and dedicated testing equipment.</div>
                            <Link to="/capabilities" className="pfc-ctac-btn cbo">Capabilities page</Link>
                        </div>
                    </div>
                </div>

                {/* Custom Project Configurator - Removed
                <CustomConfigurator answers={custAnswers} onPick={custPick} allAnswered={allAnswered} />
                */}
            </div>

            {selectedFamily && (
                <FamilyImageModal family={selectedFamily} onClose={() => setSelectedFamily(null)} />
            )}
        </ProductLayout>
    )
}

const CUST_OPTIONS = {
    1: ['Ball Valve', 'Poppet Valve', 'Butterfly Valve', 'Sleeve Valve', 'Check Valve', 'Regulator', 'Vent/Relief'],
    2: ['Flight – Launch Vehicle', 'Ground – GSE', 'Ground – Test Facility', 'Both'],
    3: ['Yes - ready to share', 'Partial - need input', 'No - PFC to scope'],
    4: ['ASAP', '1–3 months', '3–6 months', 'Exploratory'],
}

const CUST_LABELS = { 1: 'Product type', 2: 'Application', 3: 'Do you have a spec/drawing?', 4: 'Timeline' }

const CustomConfigurator = ({ answers, onPick, allAnswered }) => {
    const [showEmailForm, setShowEmailForm] = useState(false)
    const [emailForm, setEmailForm] = useState({ name: '', email: '', company: '', notes: '' })

    const buildMailtoLink = () => {
        let body = `Custom Product Inquiry\n\nSelections:\n`
        Object.entries(CUST_LABELS).forEach(([step, label]) => {
            if (answers[step]) body += `  ${label}: ${answers[step]}\n`
        })
        if (emailForm.notes) body += `\nAdditional Notes:\n${emailForm.notes}\n`
        body += `\nContact:\n`
        if (emailForm.name) body += `  Name: ${emailForm.name}\n`
        if (emailForm.company) body += `  Company: ${emailForm.company}\n`
        if (emailForm.email) body += `  Email: ${emailForm.email}\n`
        const subject = encodeURIComponent(`Custom Product Inquiry – ${answers[1] || 'General'}`)
        return `mailto:sales@precisionfluidcontrols.com?subject=${subject}&body=${encodeURIComponent(body)}`
    }

    return (
        <div className="pfc-cfg">
            <div className="pfc-cfg-hdr">
                <div className="pfc-cfg-hdr-title">Start a Custom Project</div>
                <div className="pfc-cfg-hdr-sub">Tell us what you need - we'll match you to the right approach</div>
            </div>
            <div className="pfc-cfg-steps">
                {[1, 2, 3, 4].map(step => (
                    <div className="pfc-cfg-step" key={step}>
                        <div className="pfc-step-row">
                            <div className={`pfc-step-num${answers[step] ? ' done' : ''}`}>
                                {answers[step] ? '✓' : step}
                            </div>
                            <div className="pfc-step-label">{CUST_LABELS[step]}</div>
                            {answers[step] && (
                                <div className="pfc-step-val">
                                    {answers[step].length > 14 ? answers[step].substring(0, 12) + '…' : answers[step]}
                                </div>
                            )}
                        </div>
                        <div className="pfc-cfg-opts">
                            {CUST_OPTIONS[step].map(opt => (
                                <button
                                    key={opt}
                                    className={`pfc-copt${answers[step] === opt ? ' on' : ''}`}
                                    onClick={() => { onPick(step, opt); setShowEmailForm(false) }}
                                >
                                    {opt}
                                </button>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {allAnswered && !showEmailForm && (
                <div className="pfc-result">
                    <div className="pfc-result-hdr">
                        <div className="pfc-result-eye">Recommended next step</div>
                        <div className="pfc-result-model" style={{ fontSize: '16px', lineHeight: 1.3 }}>
                            {answers[3] === 'Yes - ready to share' ? 'Send your spec to PFC engineering' : 'Schedule a requirements call'}
                        </div>
                    </div>
                    <div className="pfc-result-body">
                        <p style={{ fontSize: '12px', color: 'var(--pfc-text2)', lineHeight: 1.6, marginBottom: '14px' }}>
                            Share your requirements with PFC engineering. {answers[4] === 'ASAP' && 'Given your urgent timeline, calling directly at (916) 626-3029 will get you the fastest response.'}
                        </p>
                        <div className="pfc-result-btns">
                            <button onClick={() => setShowEmailForm(true)} className="pfc-rbtn pfc-rbtn-p" style={{ border: 'none', cursor: 'pointer' }}>Send inquiry via email</button>
                            <Link to="/contactus" className="pfc-rbtn pfc-rbtn-o">Contact form</Link>
                        </div>
                    </div>
                </div>
            )}

            {allAnswered && showEmailForm && (
                <div style={{ margin: '16px 20px 20px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '12px', color: 'var(--pfc-text)' }}>
                        Send Inquiry
                    </div>
                    <div style={{ background: 'var(--pfc-surface2)', border: '1px solid var(--pfc-rule)', borderRadius: '6px', padding: '10px 12px', marginBottom: '12px' }}>
                        <div style={{ fontSize: '9px', fontFamily: 'var(--pfc-mono)', letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--pfc-text3)', marginBottom: '6px' }}>
                            Your selections
                        </div>
                        {Object.entries(CUST_LABELS).map(([step, label]) => answers[step] ? (
                            <div key={step} style={{ fontSize: '11px', color: 'var(--pfc-text2)', padding: '2px 0', display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ color: 'var(--pfc-text3)' }}>{label}:</span>
                                <span style={{ fontWeight: 500 }}>{answers[step]}</span>
                            </div>
                        ) : null)}
                        <button
                            onClick={() => setShowEmailForm(false)}
                            style={{ marginTop: '8px', background: 'none', border: 'none', color: 'var(--pfc-plum3)', fontSize: '10px', cursor: 'pointer', padding: 0, fontFamily: 'var(--pfc-mono)' }}
                        >
                            ← Modify selections
                        </button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                        <input type="text" placeholder="Your name" value={emailForm.name} onChange={e => setEmailForm({ ...emailForm, name: e.target.value })} style={custInputStyle} />
                        <input type="email" placeholder="Email address" value={emailForm.email} onChange={e => setEmailForm({ ...emailForm, email: e.target.value })} style={custInputStyle} />
                        <input type="text" placeholder="Company" value={emailForm.company} onChange={e => setEmailForm({ ...emailForm, company: e.target.value })} style={custInputStyle} />
                        <textarea placeholder="Additional notes or requirements..." value={emailForm.notes} onChange={e => setEmailForm({ ...emailForm, notes: e.target.value })} rows={3} style={{ ...custInputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
                    </div>
                    <a href={buildMailtoLink()} className="pfc-rbtn pfc-rbtn-p" style={{ display: 'block', textAlign: 'center', textDecoration: 'none', padding: '10px 0' }}>
                        Open in email client
                    </a>
                    <div style={{ textAlign: 'center', marginTop: '6px', fontSize: '10px', color: 'var(--pfc-text3)' }}>
                        Opens your default email app with selections pre-filled
                    </div>
                </div>
            )}

            {!showEmailForm && (
                <div className="pfc-cfg-contact">
                    <div className="pfc-cfg-contact-text">Prefer to talk? Our engineers are ready.</div>
                    <div className="pfc-cfg-contact-phone">(916) 626-3029</div>
                </div>
            )}
        </div>
    )
}

const custInputStyle = {
    fontSize: '12px',
    padding: '8px 10px',
    border: '1px solid var(--pfc-rule2)',
    borderRadius: '5px',
    background: 'var(--pfc-surface)',
    color: 'var(--pfc-text)',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
}

export default CustomProductsPage
